import PropTypes from 'prop-types';
import { AspectRatio, Box } from '@chakra-ui/react';
import { useMsal } from '@azure/msal-react';
import { v4 as uuidv4 } from 'uuid';
import {
  generateActor,
  generateAuth,
  getProgress,
  LRSQL_URL,
} from 'config/lrsqlConfig';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { postInteractiveSessionAction } from 'store/modules/progression/progression-actions';
import { companionActions } from 'companion/store/companion-slice';
import { CompanionMode } from 'utils/constants';

function InteractiveContentSubQuest({
  classId,
  course,
  quest,
  subQuest,
  code,
  onComplete,
  onPostProgression,
}) {
  const baseFileUrl = process.env.REACT_APP_FILE_URL;

  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const { localAccountId } = accounts[0];

  const [userSessionId, setUserSessionId] = useState(
    `${localAccountId}-${uuidv4()}`
  );

  const { filePath, indexName, transcript, isRequirePass } =
    subQuest.subQuestInteractiveVideo;

  useEffect(() => {
    let intervalId = null;
    const fetchAuthProgress = async () => {
      const response = await getProgress(
        userSessionId,
        course.id,
        quest.id,
        subQuest.id,
        isRequirePass
      );

      if (response.length > 0) {
        if (response[0].result.score) {
          const { success, duration, score: scoreObject } = response[0].result;

          const { max: maxScore, min: passingScore, raw: score } = scoreObject;

          dispatch(
            postInteractiveSessionAction({
              courseId: course.id,
              questId: quest.id,
              subQuestId: subQuest.id,
              isSuccess: success,
              classId,
              code,
              duration,
              score,
              maxScore,
              passingScore,
            })
          );
        } else if (onPostProgression) {
          onPostProgression({
            courseId: course.id,
            questId: quest.id,
            subQuestId: subQuest.id,
            classId,
            code,
          });
        }

        if (onComplete) {
          onComplete();
        }

        setTimeout(() => {
          setUserSessionId(`${localAccountId}-${uuidv4()}`);
        }, 1000);

        clearInterval(intervalId);
      }
    };

    intervalId = setInterval(() => {
      fetchAuthProgress();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [
    localAccountId,
    userSessionId,
    classId,
    course.id,
    quest.id,
    subQuest.id,
    isRequirePass,
    code,
    dispatch,
    onComplete,
    onPostProgression,
  ]);

  useEffect(() => {
    if (course && quest && subQuest) {
      const { name, objective } = subQuest;
      let videoContext = transcript;
      if (transcript === null || transcript === '') {
        videoContext = `Course: ${course.name} - ${course.description} | `;
        videoContext += `Quest: ${quest.name} - ${quest.description} | `;
        videoContext += `Video: ${name} - ${objective}`;
      }

      dispatch(companionActions.setContext({ videoContext }));
      dispatch(companionActions.setMode(CompanionMode.VIDEO_COMPANION));
      dispatch(
        companionActions.setGreetings(
          `I'm your AI Companion Bot, ready to assist you. I noticed that you're watching a video titled '${name}'. As your virtual trainer, feel free to ask me any questions you may have. I'm here to provide helpful information and guidance. So go ahead and ask away!`
        )
      );
    }
  }, [subQuest, course, quest, transcript, dispatch]);

  const getPath = () => {
    const params = `endpoint=${LRSQL_URL}/xapi/&auth=Basic ${generateAuth()}&actor=${generateActor(userSessionId, course.id, quest.id, subQuest.id)}`;
    return `${baseFileUrl}${filePath}/${indexName}?${params}`;
  };

  return (
    <Box borderWidth="1px">
      <AspectRatio h="80vh" ratio={16 / 9}>
        <iframe title="Interactive Content" src={getPath()} />
      </AspectRatio>
    </Box>
  );
}

InteractiveContentSubQuest.defaultProps = {
  classId: null,
  onComplete: null,
  onPostProgression: null,
};

InteractiveContentSubQuest.propTypes = {
  classId: PropTypes.number,
  course: PropTypes.object.isRequired,
  quest: PropTypes.object.isRequired,
  subQuest: PropTypes.object.isRequired,
  code: PropTypes.string.isRequired,
  onComplete: PropTypes.func,
  onPostProgression: PropTypes.func,
};

export default InteractiveContentSubQuest;
